'use client';

import { c as _c } from "react/compiler-runtime";
import type { SdkWallet } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/app-popover';
import { Icon } from '@/components/ui/icon';
import { ArrowDataTransferVerticalIcon, Copy01Icon, Tick02Icon, Wallet01Icon } from '@/components/ui/icon/glyphs';
import { walletDisplayLabel } from '@/components/wallet/wallet-display-label';
import { useFormattedCurrency } from '@/lib/currency';
import { cn } from '@/lib/ui/utils';
import { copyWalletAddress } from '@/lib/wallet/copy-address';
export type CreatorWalletBalanceState = 'loading' | 'error' | 'ready';
interface CreateProposalWalletSelectorProps {
  readonly label?: string | undefined;
  readonly wallets: readonly SdkWallet[];
  readonly selectedWalletId: string | null;
  readonly balancesD6: ReadonlyMap<string, bigint>;
  readonly balanceState: CreatorWalletBalanceState;
  readonly disabled: boolean;
  readonly onWalletChange: (walletId: string) => void;
}
function WalletBalance(t0) {
  const $ = _c(17);
  const {
    wallet,
    balancesD6,
    state
  } = t0;
  const t = useTranslations("launchpad.form");
  const {
    formatD6
  } = useFormattedCurrency();
  if (state === "loading") {
    let t1;
    if ($[0] !== t) {
      t1 = t("walletBalanceLoading");
      $[0] = t;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <span aria-live="polite">{t1}</span>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (state === "error") {
    let t1;
    if ($[4] !== t) {
      t1 = t("walletBalanceUnavailable");
      $[4] = t;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    let t2;
    if ($[6] !== t1) {
      t2 = <output>{t1}</output>;
      $[6] = t1;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  let t1;
  if ($[8] !== balancesD6 || $[9] !== wallet.address) {
    t1 = balancesD6.get(wallet.address);
    $[8] = balancesD6;
    $[9] = wallet.address;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  const balanceD6 = t1;
  let t2;
  if ($[11] !== balanceD6 || $[12] !== formatD6 || $[13] !== t) {
    t2 = balanceD6 === undefined ? t("walletBalanceUnavailable") : formatD6(String(balanceD6));
    $[11] = balanceD6;
    $[12] = formatD6;
    $[13] = t;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  let t3;
  if ($[15] !== t2) {
    t3 = <span>{t2}</span>;
    $[15] = t2;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  return t3;
}
function WalletOption(t0) {
  const $ = _c(31);
  const {
    wallet,
    selected,
    balancesD6,
    balanceState,
    disabled,
    onSelect
  } = t0;
  const tWallet = useTranslations("wallet.table");
  let t1;
  if ($[0] !== wallet) {
    t1 = walletDisplayLabel(wallet);
    $[0] = wallet;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const label = t1;
  const t2 = selected ? "opacity-100" : "opacity-0";
  let t3;
  if ($[2] !== t2) {
    t3 = cn("size-4 shrink-0", t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <Icon icon={Tick02Icon} className={t3} aria-hidden="true" />;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== label) {
    t5 = <span className="min-w-0 flex-1 truncate text-left">{label}</span>;
    $[6] = label;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== balanceState || $[9] !== balancesD6 || $[10] !== wallet) {
    t6 = <span className="shrink-0 text-caption text-foreground-secondary tabular-nums"><WalletBalance wallet={wallet} balancesD6={balancesD6} state={balanceState} /></span>;
    $[8] = balanceState;
    $[9] = balancesD6;
    $[10] = wallet;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== disabled || $[13] !== onSelect || $[14] !== selected || $[15] !== t4 || $[16] !== t5 || $[17] !== t6) {
    t7 = <AnimatedButton type="button" variant="tertiary" disabled={disabled} onClick={onSelect} aria-pressed={selected} className="min-w-0 flex-1 justify-start px-2 font-normal">{t4}{t5}{t6}</AnimatedButton>;
    $[12] = disabled;
    $[13] = onSelect;
    $[14] = selected;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== tWallet) {
    t8 = tWallet("copy_address");
    $[19] = tWallet;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== label || $[22] !== wallet.address) {
    t9 = () => copyWalletAddress(wallet.address, label);
    $[21] = label;
    $[22] = wallet.address;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Icon icon={Copy01Icon} className="size-4" aria-hidden="true" />;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t8 || $[26] !== t9) {
    t11 = <AnimatedButton type="button" variant="tertiary" size="sm" aria-label={t8} onClick={t9}>{t10}</AnimatedButton>;
    $[25] = t8;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t11 || $[29] !== t7) {
    t12 = <div className="flex min-w-0 items-center gap-1">{t7}{t11}</div>;
    $[28] = t11;
    $[29] = t7;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  return t12;
}
export function CreateProposalWalletSelector(t0) {
  const $ = _c(49);
  const {
    label,
    wallets,
    selectedWalletId,
    balancesD6,
    balanceState,
    disabled,
    onWalletChange
  } = t0;
  const t = useTranslations("launchpad.form");
  const [open, setOpen] = useState(false);
  let selectedWallet;
  let t1;
  let t2;
  if ($[0] !== balanceState || $[1] !== balancesD6 || $[2] !== label || $[3] !== selectedWalletId || $[4] !== t || $[5] !== wallets) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t3;
      if ($[9] !== selectedWalletId) {
        t3 = wallet => wallet.id === selectedWalletId;
        $[9] = selectedWalletId;
        $[10] = t3;
      } else {
        t3 = $[10];
      }
      selectedWallet = wallets.find(t3);
      if (wallets.length === 1 && selectedWallet !== undefined) {
        let t4;
        if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = <Icon icon={Wallet01Icon} className="size-3.5 shrink-0" aria-hidden="true" />;
          $[11] = t4;
        } else {
          t4 = $[11];
        }
        const t5 = <span className="flex min-w-0 items-center gap-2 text-foreground-secondary">{t4}<span className="truncate">{label ?? walletDisplayLabel(selectedWallet)}</span></span>;
        const t6 = <span className="shrink-0 font-medium text-foreground-primary tabular-nums"><WalletBalance wallet={selectedWallet} balancesD6={balancesD6} state={balanceState} /></span>;
        let t7;
        if ($[12] !== t5 || $[13] !== t6) {
          t7 = <div className="flex min-w-0 items-center justify-between gap-3 pl-1 text-body">{t5}{t6}</div>;
          $[12] = t5;
          $[13] = t6;
          $[14] = t7;
        } else {
          t7 = $[14];
        }
        t2 = t7;
        break bb0;
      }
      t1 = label ?? (selectedWallet === undefined ? t("selectCreatorWallet") : walletDisplayLabel(selectedWallet));
    }
    $[0] = balanceState;
    $[1] = balancesD6;
    $[2] = label;
    $[3] = selectedWalletId;
    $[4] = t;
    $[5] = wallets;
    $[6] = selectedWallet;
    $[7] = t1;
    $[8] = t2;
  } else {
    selectedWallet = $[6];
    t1 = $[7];
    t2 = $[8];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  const triggerLabel = t1;
  const t3 = disabled || wallets.length === 0;
  let t4;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Icon icon={Wallet01Icon} className="size-4 shrink-0" aria-hidden="true" />;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== triggerLabel) {
    t5 = <span className="flex min-w-0 items-center gap-2">{t4}<span className="truncate">{triggerLabel}</span></span>;
    $[16] = triggerLabel;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== balanceState || $[19] !== balancesD6 || $[20] !== selectedWallet) {
    t6 = selectedWallet !== undefined && <WalletBalance wallet={selectedWallet} balancesD6={balancesD6} state={balanceState} />;
    $[18] = balanceState;
    $[19] = balancesD6;
    $[20] = selectedWallet;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Icon icon={ArrowDataTransferVerticalIcon} className="size-4" aria-hidden="true" />;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== t6) {
    t8 = <span className="flex shrink-0 items-center gap-2 text-caption text-foreground-secondary tabular-nums">{t6}{t7}</span>;
    $[23] = t6;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== open || $[26] !== t3 || $[27] !== t5 || $[28] !== t8) {
    t9 = <PopoverTrigger asChild={true}><AnimatedButton type="button" variant="secondary" disabled={t3} aria-expanded={open} aria-haspopup="dialog" className="w-full min-w-0 justify-between font-normal">{t5}{t8}</AnimatedButton></PopoverTrigger>;
    $[25] = open;
    $[26] = t3;
    $[27] = t5;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== balanceState || $[31] !== balancesD6 || $[32] !== disabled || $[33] !== onWalletChange || $[34] !== selectedWalletId || $[35] !== wallets) {
    let t11;
    if ($[37] !== balanceState || $[38] !== balancesD6 || $[39] !== disabled || $[40] !== onWalletChange || $[41] !== selectedWalletId) {
      t11 = wallet_0 => <WalletOption key={wallet_0.id} wallet={wallet_0} selected={wallet_0.id === selectedWalletId} balancesD6={balancesD6} balanceState={balanceState} disabled={disabled} onSelect={() => {
        onWalletChange(wallet_0.id);
        setOpen(false);
      }} />;
      $[37] = balanceState;
      $[38] = balancesD6;
      $[39] = disabled;
      $[40] = onWalletChange;
      $[41] = selectedWalletId;
      $[42] = t11;
    } else {
      t11 = $[42];
    }
    t10 = wallets.map(t11);
    $[30] = balanceState;
    $[31] = balancesD6;
    $[32] = disabled;
    $[33] = onWalletChange;
    $[34] = selectedWalletId;
    $[35] = wallets;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  let t11;
  if ($[43] !== t10) {
    t11 = <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] min-w-0 p-1"><div className="flex flex-col gap-1">{t10}</div></PopoverContent>;
    $[43] = t10;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  let t12;
  if ($[45] !== open || $[46] !== t11 || $[47] !== t9) {
    t12 = <Popover open={open} onOpenChange={setOpen}>{t9}{t11}</Popover>;
    $[45] = open;
    $[46] = t11;
    $[47] = t9;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  return t12;
}